<template>
  <div>
    <PageHeaderLayout>
      <div class="main-page-content">
        <el-card shadow="never" class="backup-card">
          <div class="backup-header">
            <i class="el-icon-folder-opened"></i>
            <span>系统备份</span>
          </div>
          <div class="backup-desc">
            备份内容：数据库 SQL、上传文件（assetslib）、应用目录（app/web/www）。<br>
            不包含：日志、历史 ZIP、backup/mysql/redis/node_modules 等大目录。
          </div>
          <el-button
            type="primary"
            icon="el-icon-download"
            :loading="backupLoading"
            @click="downloadBackup"
          >
            {{ backupLoading ? '正在生成备份...' : '开始系统备份' }}
          </el-button>
          <div class="backup-status" v-if="taskStatus.message">
            <span>任务状态：{{ taskStatus.status || '-' }}（{{ taskStatus.progress || 0 }}%）</span>
            <span>{{ taskStatus.message }}</span>
            <span v-if="taskStatus.error" class="error-text">{{ taskStatus.error }}</span>
          </div>
        </el-card>

        <el-card shadow="never" class="backup-card">
          <div class="backup-header">
            <i class="el-icon-tickets"></i>
            <span>备份文件列表</span>
          </div>
          <div class="backup-desc">
            展示服务器备份目录中已有的 ZIP 文件，可直接下载。
          </div>
          <div class="list-actions">
            <el-button
              size="mini"
              icon="el-icon-refresh"
              :loading="listLoading"
              @click="loadBackupList"
            >
              刷新列表
            </el-button>
          </div>
          <el-table
            :data="backupFiles"
            size="mini"
            border
            v-loading="listLoading"
            empty-text="暂无备份文件"
          >
            <el-table-column prop="file_name" label="文件名称" min-width="260" show-overflow-tooltip />
            <el-table-column prop="backup_time" label="备份时间" width="180" />
            <el-table-column label="文件大小" width="120">
              <template slot-scope="scope">
                {{ formatFileSize(scope.row.file_size) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="160" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" @click="downloadByFileName(scope.row.file_name)">下载</el-button>
                <el-button type="text" style="color:#f56c6c" @click="deleteBackupFile(scope.row.file_name)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>

        <el-card shadow="never" class="backup-card">
          <div class="backup-header">
            <i class="el-icon-document"></i>
            <span>日志文件浏览</span>
          </div>
          <div class="backup-desc">
            可下载 `/ola/app/logs` 下日志文件，支持实时查看最后 500 行（约每 10 秒刷新，可暂停）。
          </div>
          <div class="list-actions log-actions">
            <el-button size="mini" icon="el-icon-refresh" :loading="logListLoading" @click="loadLogList">刷新日志列表</el-button>
            <el-select
              v-model="selectedLogFile"
              size="mini"
              filterable
              clearable
              placeholder="选择日志文件"
              style="width: 320px;"
              @change="onLogFileChange"
            >
              <el-option v-for="f in logFiles" :key="f.file_name" :label="f.file_name" :value="f.file_name" />
            </el-select>
            <el-button size="mini" type="success" icon="el-icon-video-play" @click="startLogAutoRefresh">开始刷新</el-button>
            <el-button size="mini" type="warning" icon="el-icon-video-pause" @click="pauseLogAutoRefresh">暂停</el-button>
            <el-button size="mini" type="primary" icon="el-icon-download" :disabled="!selectedLogFile" @click="downloadLogFile">下载日志</el-button>
            <el-button size="mini" icon="el-icon-document-copy" :disabled="!selectedLogFile || !logTailContent" @click="copyLogRefreshContent">复制刷新内容</el-button>
          </div>
          <div class="backup-status" v-if="selectedLogFile">
            <span>当前文件：{{ selectedLogFile }}</span>
            <span>最后刷新：{{ logLastUpdated || '-' }}</span>
            <span>刷新状态：{{ logAutoRefresh ? '自动刷新中' : '已暂停' }}</span>
          </div>
          <el-input
            type="textarea"
            :rows="22"
            readonly
            v-model="logTailContent"
            placeholder="请选择日志文件后查看最后500行日志"
            class="log-tail-area"
          />

          <div class="log-analyze-panel">
            <div class="backup-header sub-header">
              <i class="el-icon-data-analysis"></i>
              <span>日志分析与搜索（服务端过滤）</span>
            </div>
            <div class="list-actions log-actions">
              <el-input v-model.trim="logAnalyzeKeyword" size="mini" clearable placeholder="分析关键词（可空）" style="width: 220px;" />
              <el-input-number v-model="logAnalyzeLinesLimit" :min="1000" :max="200000" :step="1000" size="mini" controls-position="right" />
              <el-button size="mini" type="primary" :loading="logAnalyzeLoading" @click="loadLogAnalyze">刷新统计</el-button>
              <el-input v-model.trim="logSearchKeyword" size="mini" clearable placeholder="关键词搜索（必填）" style="width: 260px;" @keyup.enter.native="searchLogContent(0)" />
              <el-button size="mini" type="success" :loading="logSearchLoading" @click="searchLogContent(0)">搜索</el-button>
              <el-button size="mini" icon="el-icon-document-copy" :disabled="!selectedLogFile" @click="copyLogAnalyzeAndSearchContent">复制分析与搜索内容</el-button>
            </div>

            <div class="metrics-grid" v-if="logStats">
              <div class="metric-card"><span>扫描行数</span><strong>{{ logStats.total_scanned_lines || 0 }}</strong></div>
              <div class="metric-card"><span>分析窗口</span><strong>{{ logStats.analyzed_lines || 0 }}</strong></div>
              <div class="metric-card"><span>唯一IP数</span><strong>{{ logStats.unique_ip_count || 0 }}</strong></div>
              <div class="metric-card"><span>错误行数</span><strong class="error-text">{{ logStats.error_line_count || 0 }}</strong></div>
              <div class="metric-card"><span>关键词命中</span><strong>{{ logStats.keyword_hits || 0 }}</strong></div>
            </div>

            <div class="analysis-tables" v-if="logStats">
              <el-table :data="logStats.top_ips || []" size="mini" border max-height="220" empty-text="暂无IP统计">
                <el-table-column prop="key" label="IP" min-width="180" />
                <el-table-column prop="count" label="次数" width="90" />
              </el-table>
              <el-table :data="logStats.time_buckets || []" size="mini" border max-height="220" empty-text="暂无时间统计">
                <el-table-column prop="key" label="时间段（小时）" min-width="180" />
                <el-table-column prop="count" label="次数" width="90" />
              </el-table>
              <el-table :data="logStats.level_stats || []" size="mini" border max-height="220" empty-text="暂无等级统计">
                <el-table-column prop="key" label="等级" min-width="120" />
                <el-table-column prop="count" label="次数" width="90" />
              </el-table>
            </div>

            <el-table :data="(logStats && logStats.error_samples) || []" size="mini" border max-height="240" empty-text="暂无错误样例">
              <el-table-column prop="line_no" label="行号" width="80" />
              <el-table-column prop="content" label="错误样例（前50条）" min-width="520" show-overflow-tooltip />
            </el-table>

            <div class="backup-status" v-if="logSearchKeyword">
              <span>搜索关键词：{{ logSearchKeyword }}</span>
              <span>命中总数：{{ logSearchTotal }}</span>
              <span>分页：offset={{ logSearchOffset }} limit={{ logSearchLimit }}</span>
            </div>
            <el-table :data="logSearchResults" size="mini" border max-height="300" empty-text="暂无搜索结果">
              <el-table-column prop="line_no" label="行号" width="80" />
              <el-table-column prop="content" label="命中内容" min-width="580" show-overflow-tooltip />
            </el-table>
            <div class="list-actions log-actions">
              <el-button size="mini" :disabled="logSearchOffset <= 0 || logSearchLoading" @click="searchLogContent(Math.max(0, logSearchOffset - logSearchLimit))">上一页</el-button>
              <el-button size="mini" :disabled="logSearchLoading || (logSearchOffset + logSearchLimit) >= logSearchTotal" @click="searchLogContent(logSearchOffset + logSearchLimit)">下一页</el-button>
            </div>
          </div>
        </el-card>
      </div>
    </PageHeaderLayout>
  </div>
</template>

<script>
import PageHeaderLayout from '@/layouts/PageHeaderLayout'
import { getApiKey } from '@/utils/apiKey'

const API_KEY = getApiKey()
const LOG_TAIL_LINES = 500
const LOG_TAIL_REFRESH_MS = 10000

export default {
  components: {
    PageHeaderLayout
  },
  data () {
    return {
      backupLoading: false,
      listLoading: false,
      backupFiles: [],
      taskStatus: {
        status: '',
        progress: 0,
        message: '',
        error: ''
      },
      logListLoading: false,
      logTailLoading: false,
      logFiles: [],
      selectedLogFile: '',
      logTailContent: '',
      logLastUpdated: '',
      logAutoRefresh: false,
      logRefreshTimer: null,
      logAnalyzeLoading: false,
      logSearchLoading: false,
      logAnalyzeKeyword: '',
      logAnalyzeLinesLimit: 50000,
      logStats: null,
      logSearchKeyword: '',
      logSearchOffset: 0,
      logSearchLimit: 100,
      logSearchTotal: 0,
      logSearchResults: []
    }
  },
  created () {
    this.loadBackupList()
    this.loadLogList()
  },
  beforeDestroy () {
    this.stopLogRefreshTimer()
  },
  methods: {
    pickResultList (resp) {
      const data = (resp && resp.data) || {}
      if (Array.isArray(data.list)) return data.list
      if (Array.isArray(data.result_list)) return data.result_list
      if (Array.isArray(resp && resp.result_list)) return resp.result_list
      return []
    },
    getDownloadFileName (resp) {
      const disposition = resp.headers.get('content-disposition') || ''
      const match = disposition.match(/filename="?([^"]+)"?/)
      if (match && match[1]) return decodeURIComponent(match[1])
      return `ola_backup_${Date.now()}.zip`
    },
    buildHeaders () {
      const token = localStorage.getItem('Authorization') || ''
      return {
        'content-type': 'application/json',
        'x-api-key': API_KEY,
        Authorization: token
      }
    },
    async fetchJson (url, payload) {
      const resp = await fetch(url, {
        method: 'POST',
        headers: this.buildHeaders(),
        body: JSON.stringify(payload || {})
      })
      const text = await resp.text()
      let json = {}
      try {
        json = text ? JSON.parse(text) : {}
      } catch (e) {
        throw new Error(text || '接口返回异常')
      }
      if (!resp.ok) {
        throw new Error(json.message || `请求失败，HTTP ${resp.status}`)
      }
      if (json.code !== 200) {
        throw new Error(json.message || '请求失败')
      }
      return json
    },
    async downloadByTask (taskId) {
      await this.downloadFromApi('/service/system/backup/task_download', { task_id: taskId })
    },
    async downloadByFileName (fileName) {
      try {
        await this.downloadFromApi('/service/system/backup/file_download', { file_name: fileName })
      } catch (e) {
        this.$message.error((e && e.message) ? e.message : '下载失败')
      }
    },
    async deleteBackupFile (fileName) {
      try {
        await this.$confirm(`确定删除备份文件 ${fileName} 吗？删除后不可恢复。`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return
      }
      try {
        await this.fetchJson('/service/system/backup/delete', { file_name: fileName })
        this.$message.success('删除成功')
        await this.loadBackupList()
      } catch (e) {
        this.$message.error((e && e.message) ? e.message : '删除失败')
      }
    },
    async downloadLogFile () {
      if (!this.selectedLogFile) {
        this.$message.warning('请先选择日志文件')
        return
      }
      await this.downloadFromApi('/service/system/log/file_download', { file_name: this.selectedLogFile })
    },
    async downloadFromApi (url, payload) {
      // 备份接口改为返回一次性 download_url，由浏览器直接流式下载（避免 blob 卡死）
      if (url.indexOf('/system/backup/') >= 0 && (url.indexOf('file_download') >= 0 || url.indexOf('task_download') >= 0)) {
        const json = await this.fetchJson(url, payload || {})
        const data = (json && json.data) || {}
        const downloadUrl = data.download_url
        if (!downloadUrl) {
          throw new Error('未获取到下载地址')
        }
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = data.file_name || ''
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        return
      }
      const resp = await fetch(url, {
        method: 'POST',
        headers: this.buildHeaders(),
        body: JSON.stringify(payload || {})
      })
      const contentType = (resp.headers.get('content-type') || '').toLowerCase()
      if (contentType.includes('application/json')) {
        const text = await resp.text()
        let msg = '下载失败'
        try {
          const json = JSON.parse(text)
          msg = json.message || msg
        } catch (e) {
          msg = text || msg
        }
        throw new Error(msg)
      }
      const blob = await resp.blob()
      const fileName = this.getDownloadFileName(resp)
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    },
    formatFileSize (size) {
      const value = Number(size) || 0
      if (value < 1024) return `${value} B`
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(2)} KB`
      if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(2)} MB`
      return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`
    },
    async loadBackupList () {
      this.listLoading = true
      try {
        const resp = await this.fetchJson('/service/system/backup/list', {})
        this.backupFiles = this.pickResultList(resp)
      } catch (e) {
        this.$message.error(e.message || '获取备份列表失败')
      } finally {
        this.listLoading = false
      }
    },
    async loadLogList () {
      this.logListLoading = true
      try {
        const resp = await this.fetchJson('/service/system/log/list', {})
        const rawList = this.pickResultList(resp)
        this.logFiles = rawList
          .map((item) => {
            const fileName = (item && (item.file_name || item.fileName || item.name || item.filename || '')) || ''
            return {
              file_name: String(fileName).trim(),
              file_size: Number((item && (item.file_size || item.fileSize)) || 0),
              update_time: (item && (item.update_time || item.updateTime)) || ''
            }
          })
          .filter((item) => item.file_name !== '')
      } catch (e) {
        this.$message.error(e.message || '获取日志列表失败')
      } finally {
        this.logListLoading = false
      }
    },
    stopLogRefreshTimer () {
      if (this.logRefreshTimer) {
        clearInterval(this.logRefreshTimer)
        this.logRefreshTimer = null
      }
    },
    async loadLogTail () {
      if (!this.selectedLogFile) return
      this.logTailLoading = true
      try {
        const resp = await this.fetchJson('/service/system/log/tail', { file_name: this.selectedLogFile, lines: LOG_TAIL_LINES })
        const data = resp.data || {}
        this.logTailContent = data.content || ''
        this.logLastUpdated = data.last_updated || ''
      } catch (e) {
        this.$message.error(e.message || '读取日志失败')
      } finally {
        this.logTailLoading = false
      }
    },
    async onLogFileChange () {
      if (!this.selectedLogFile) {
        this.logTailContent = ''
        this.logLastUpdated = ''
        this.logStats = null
        this.logSearchResults = []
        this.logSearchTotal = 0
        this.pauseLogAutoRefresh()
        return
      }
      await this.loadLogTail()
      await this.loadLogAnalyze()
      this.startLogAutoRefresh()
    },
    pickResultDetail (resp) {
      const data = (resp && resp.data) || {}
      return data.detail || data.result_detail || resp.result_detail || {}
    },
    async loadLogAnalyze () {
      if (!this.selectedLogFile) {
        this.$message.warning('请先选择日志文件')
        return
      }
      this.logAnalyzeLoading = true
      try {
        const resp = await this.fetchJson('/service/system/log/analyze', {
          file_name: this.selectedLogFile,
          lines_limit: this.logAnalyzeLinesLimit,
          keyword: this.logAnalyzeKeyword
        })
        this.logStats = this.pickResultDetail(resp)
      } catch (e) {
        this.$message.error(e.message || '日志分析失败')
      } finally {
        this.logAnalyzeLoading = false
      }
    },
    async searchLogContent (offset = 0) {
      if (!this.selectedLogFile) {
        this.$message.warning('请先选择日志文件')
        return
      }
      if (!this.logSearchKeyword) {
        this.$message.warning('请输入搜索关键词')
        return
      }
      this.logSearchLoading = true
      try {
        this.logSearchOffset = offset
        const resp = await this.fetchJson('/service/system/log/search', {
          file_name: this.selectedLogFile,
          keyword: this.logSearchKeyword,
          lines_limit: this.logAnalyzeLinesLimit,
          offset: this.logSearchOffset,
          limit: this.logSearchLimit,
          ignore_case: true
        })
        const detail = this.pickResultDetail(resp)
        this.logSearchTotal = Number(detail.total_matches || 0)
        this.logSearchResults = detail.result_list || []
      } catch (e) {
        this.$message.error(e.message || '日志搜索失败')
      } finally {
        this.logSearchLoading = false
      }
    },
    async startLogAutoRefresh () {
      if (!this.selectedLogFile) {
        this.$message.warning('请先选择日志文件')
        return
      }
      this.logAutoRefresh = true
      await this.loadLogTail()
      this.stopLogRefreshTimer()
      this.logRefreshTimer = setInterval(() => {
        if (!this.logAutoRefresh || !this.selectedLogFile) return
        this.loadLogTail()
      }, LOG_TAIL_REFRESH_MS)
    },
    pauseLogAutoRefresh () {
      this.logAutoRefresh = false
      this.stopLogRefreshTimer()
    },
    async copyTextToClipboard (text, successTip) {
      const payload = text == null ? '' : String(text)
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(payload)
        } else {
          const ta = document.createElement('textarea')
          ta.value = payload
          ta.setAttribute('readonly', 'readonly')
          ta.style.position = 'fixed'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.$message.success(successTip || '已复制到剪贴板')
      } catch (e) {
        this.$message.error((e && e.message) || '复制失败')
      }
    },
    async copyLogRefreshContent () {
      if (!this.selectedLogFile) {
        this.$message.warning('请先选择日志文件')
        return
      }
      const body = this.logTailContent || ''
      if (!body.trim()) {
        this.$message.warning('当前没有可复制的日志内容，请先开始刷新或等待加载')
        return
      }
      const header =
        `【日志尾部 · 自动刷新区】\n` +
        `文件: ${this.selectedLogFile}\n` +
        `最后刷新: ${this.logLastUpdated || '-'}\n` +
        `刷新状态: ${this.logAutoRefresh ? '自动刷新中' : '已暂停'}\n` +
        `---\n`
      await this.copyTextToClipboard(header + body, '已复制当前页面显示的日志尾部内容')
    },
    buildLogAnalyzeReportText () {
      const lines = []
      lines.push('【日志分析与搜索 · 服务端过滤结果汇总】')
      lines.push(`日志文件: ${this.selectedLogFile || '-'}`)
      lines.push(`分析关键词（统计用）: ${this.logAnalyzeKeyword || '(空)'}`)
      lines.push(`分析扫描行数上限: ${this.logAnalyzeLinesLimit}`)
      lines.push('')
      const s = this.logStats
      if (s) {
        lines.push('=== 概览指标 ===')
        lines.push(`扫描行数: ${s.total_scanned_lines || 0}`)
        lines.push(`分析窗口行数: ${s.analyzed_lines || 0}`)
        lines.push(`唯一 IP 数: ${s.unique_ip_count || 0}`)
        lines.push(`错误行数: ${s.error_line_count || 0}`)
        lines.push(`关键词命中（统计）: ${s.keyword_hits || 0}`)
        lines.push('')
        const fmtPairs = (title, rows) => {
          lines.push(`=== ${title} ===`)
          if (!rows || !rows.length) {
            lines.push('(无)')
            lines.push('')
            return
          }
          rows.forEach((r) => {
            lines.push(`${r.key}\t${r.count}`)
          })
          lines.push('')
        }
        fmtPairs('Top IP', s.top_ips)
        fmtPairs('时间段（小时）', s.time_buckets)
        fmtPairs('日志等级', s.level_stats)
        lines.push('=== 错误样例（最多50条） ===')
        const errs = s.error_samples || []
        if (!errs.length) {
          lines.push('(无)')
        } else {
          errs.forEach((row) => {
            lines.push(`L${row.line_no}\t${row.content}`)
          })
        }
        lines.push('')
      } else {
        lines.push('(尚未加载统计分析，请先选择文件并点击「刷新统计」)')
        lines.push('')
      }
      lines.push('=== 关键词搜索（服务端过滤） ===')
      lines.push(`搜索关键词: ${this.logSearchKeyword || '(未搜索)'}`)
      lines.push(`命中总数: ${this.logSearchTotal}`)
      lines.push(`当前分页 offset=${this.logSearchOffset} limit=${this.logSearchLimit}`)
      const rs = this.logSearchResults || []
      if (!rs.length) {
        lines.push('当前页结果: (无)')
      } else {
        lines.push('当前页命中行:')
        rs.forEach((row) => {
          lines.push(`L${row.line_no}\t${row.content}`)
        })
      }
      return lines.join('\n')
    },
    async copyLogAnalyzeAndSearchContent () {
      if (!this.selectedLogFile) {
        this.$message.warning('请先选择日志文件')
        return
      }
      const text = this.buildLogAnalyzeReportText()
      await this.copyTextToClipboard(text, '已复制日志分析与搜索（服务端过滤）全文')
    },
    async downloadBackup () {
      this.backupLoading = true
      this.taskStatus = { status: 'pending', progress: 0, message: '正在创建备份任务...', error: '' }
      try {
        const createRes = await this.fetchJson('/service/system/backup/create_task', {})
        const taskId = createRes.data && createRes.data.task_id
        if (!taskId) throw new Error('未获取到任务ID')
        let finish = false
        const maxRounds = 120
        for (let i = 0; i < maxRounds; i++) {
          await new Promise(resolve => setTimeout(resolve, 2000))
          const statusRes = await this.fetchJson('/service/system/backup/task_status', { task_id: taskId })
          const data = statusRes.data || {}
          this.taskStatus = {
            status: data.status || '',
            progress: data.progress || 0,
            message: data.message || '',
            error: data.error || ''
          }
          if (data.status === 'failed') {
            throw new Error(data.error || data.message || '备份任务失败')
          }
          if (data.status === 'success') {
            await this.downloadByTask(taskId)
            await this.loadBackupList()
            finish = true
            this.$message.success('系统备份下载成功')
            break
          }
        }
        if (!finish) {
          throw new Error('备份任务超时，请稍后重试')
        }
      } catch (e) {
        this.taskStatus.error = e.message || '系统备份失败'
        this.$message.error(e.message || '系统备份失败')
      } finally {
        this.backupLoading = false
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.backup-card
  margin-top 12px
  padding 12px

.list-actions
  margin-bottom 10px

.log-actions
  display flex
  align-items center
  gap 8px
  flex-wrap wrap

.backup-header
  display flex
  align-items center
  gap 8px
  font-size 18px
  font-weight 600
  color #303133
  margin-bottom 10px

.backup-desc
  margin-bottom 16px
  color #606266
  line-height 1.6

.backup-status
  margin-top 12px
  display flex
  flex-direction column
  gap 4px
  color #606266

.error-text
  color #f56c6c

.log-tail-area
  margin-top 10px
  /deep/ textarea
    font-family Consolas, "Courier New", monospace
    font-size 12px

.log-analyze-panel
  margin-top 14px

.sub-header
  font-size 16px
  margin-top 6px

.metrics-grid
  display grid
  grid-template-columns repeat(auto-fill, minmax(150px, 1fr))
  gap 8px
  margin-bottom 10px

.metric-card
  border 1px solid #ebeef5
  border-radius 4px
  padding 8px 10px
  display flex
  justify-content space-between
  align-items center
  background #fafafa

.analysis-tables
  display grid
  grid-template-columns repeat(auto-fill, minmax(240px, 1fr))
  gap 8px
  margin-bottom 10px
</style>
